/* eslint-disable no-shadow */
import cloneDeep from 'lodash/cloneDeep';
import Transformer from '@/transformers/ProductTransformer';
import Proxy from '@/proxies/SubstoreCatalogueProxy';
import Footnotes from '@/utils/Footnotes';

export const getDefaultState = () => ({
  productListRaw: [],
  productList: [],
  productListFiltered: [],
  filterProducts: [],
  footnotes: null,
  filterProductSelected: '',
  lastPromise: null,
  isLoaded: false,
});

export const state = getDefaultState();

export const types = {
  RESET: 'RESET',
  RESET_PRODUCTLIST: 'RESET_PRODUCTLIST',
  SET_PRODUCTLIST: 'SET_PRODUCTLIST',
  LOAD_NEXT: 'LOAD_NEXT',
  SET_PRODUCTLIST_FILTERED: 'SET_PRODUCTLIST_FILTERED',
  RESET_FILTER: 'RESET_FILTER',
  SET_LAST_PROMISE: 'SET_LAST_PROMISE',
  UPDATE_PRODUCT_FILTER: 'UPDATE_PRODUCT_FILTER',
};

const itemsPerPage = 4;

export const mutations = {
  [types.RESET](state) {
    Object.assign(state, getDefaultState());
  },

  [types.RESET_PRODUCTLIST](state) {
    state.productList = [];
    state.productListRaw = [];
    state.productListFiltered = [];
    state.filterProducts = [];
    state.footnotes = null;
    state.lastPromise = null;
    state.isLoaded = false;
  },

  [types.SET_PRODUCTLIST](state, payload) {
    state.productList = payload.productList;
    state.productListRaw = payload.productListRaw;
    state.productListFiltered = payload.productListFiltered;
    state.filterProducts = payload.filterProducts;
    state.footnotes = payload.footnotes;
    state.isLoaded = true;
  },

  [types.LOAD_NEXT](state) {
    state.productList = state.productListFiltered.slice(0, state.productList.length + itemsPerPage);
  },

  [types.SET_PRODUCTLIST_FILTERED](state, { productListFiltered, footnotes }) {
    state.productList = [];
    state.productListFiltered = productListFiltered;
    state.footnotes = footnotes;
  },

  [types.RESET_FILTER](state) {
    state.filterProductSelected = '';
  },

  [types.SET_LAST_PROMISE](state, value) {
    state.lastPromise = value;
  },

  [types.UPDATE_PRODUCT_FILTER](state, value) {
    state.filterProductSelected = value;
  },
};

export const getters = {
  hasNextItems: state => (state.productList ? state.productList.length < state.productListFiltered.length : false),

  totalItems: state => (state.productListFiltered ? state.productListFiltered.length : 0),
};

export const actions = {
  reset({ commit }) {
    commit(types.RESET);
  },

  getProductList({ commit, state, dispatch }) {
    commit(types.RESET_PRODUCTLIST);
    const promise = new Promise((resolve, reject) => {
      new Proxy()
        .all()
        .then(response => {
          if (state.lastPromise !== promise) return; // dismiss old response

          if (response.head.fault !== null) {
            reject(response.head.fault);
          }

          const payload = {
            productList: [],
            productListFiltered: [],
            productListRaw: response.data.productList,
            filterProducts: response.data.availableFilters.filter(filter => filter.type === 'product'),
            footnotes: null,
          };

          const filterInitial = response.data.availableFilters.find(filter => filter.initial);
          if (filterInitial && filterInitial.type === 'product') {
            dispatch('updateProductFilter', filterInitial.id);
          }

          commit(types.SET_PRODUCTLIST, payload);

          dispatch('applyFilter');

          resolve(payload);
        })
        .catch(error => {
          if (state.lastPromise !== promise) return; // dismiss old response
          reject(error);
        });
    });

    commit(types.SET_LAST_PROMISE, promise);

    return promise;
  },

  async loadNext({ commit, state }) {
    await state.lastPromise;

    commit(types.LOAD_NEXT);
  },

  applyFilter({ commit, state }) {
    const footnotes = new Footnotes();
    let productListFiltered = cloneDeep(state.productListRaw);

    if (state.filterProductSelected !== '') {
      productListFiltered = productListFiltered.filter(product => product.belongsToFilters.indexOf(state.filterProductSelected) > -1);
    }

    productListFiltered = Transformer.fetchCollection(productListFiltered, footnotes);

    commit(types.SET_PRODUCTLIST_FILTERED, { productListFiltered, footnotes });
  },

  resetFilter({ commit, dispatch }) {
    commit(types.RESET_FILTER);
    dispatch('applyFilter');
  },

  updateProductFilter({ commit }, value) {
    commit(types.UPDATE_PRODUCT_FILTER, value);
  },
};
